// Check-in analytics — config + extractor. Inherits the shared engine/dashboard.
//
// mood/energy are 1-5 scales and sleep_hours is hours → `avg` reducers (a day
// with no check-in is "no data", not "mood 0"). No streak metric: the check-in
// StatsView already shows an engine-canonical streak flame, so a card would
// duplicate it. The dashboard adds day/week/month averages + trend + projection.
//
// GUARDRAIL (Check-in = no clinical claim): declares only average mood/energy
// and their trend. The dashboard renders ▲/▼ and a neutral projection — never a
// mental-health assertion or advice.

import type { Sample } from "./engine";
import type { DashboardConfig } from "./dashboard";
import type { CheckInRecord } from "@/hooks/useCheckIn";
import { checkInAnalyticsTime } from "@/lib/checkInDates";

export const CHECKIN_DASHBOARD: DashboardConfig = {
  moduleKey: "checkin",
  titleKey: "checkin.analytics.title",
  defaultGranularity: "week",
  metrics: [
    {
      key: "mood",
      labelKey: "checkin.analytics.metric.mood",
      reducer: "avg",
      color: "hsl(265 60% 65%)",
      chart: "line",
      projectable: true,
      format: (v) => v.toFixed(1),
    },
    {
      key: "energy",
      labelKey: "checkin.analytics.metric.energy",
      reducer: "avg",
      color: "hsl(35 85% 55%)",
      chart: "line",
      projectable: true,
      format: (v) => v.toFixed(1),
    },
    {
      key: "sleep_hours",
      labelKey: "checkin.analytics.metric.sleepHours",
      reducer: "avg",
      color: "hsl(220 70% 62%)",
      unitKey: "analytics.unit.h",
      chart: "line",
      projectable: true,
      format: (v) => v.toFixed(1),
    },
  ],
};

export function extractCheckinSamples(rows: CheckInRecord[]): Record<string, Sample[]> {
  const mood: Sample[] = [];
  const energy: Sample[] = [];
  const sleep_hours: Sample[] = [];

  for (const r of rows) {
    if (!r.checked_in_at) continue;
    const t = checkInAnalyticsTime(r.checked_in_at);
    if (t === null) continue;
    const category = r.sleep_quality ?? undefined;

    if (typeof r.mood === "number" && Number.isFinite(r.mood) && r.mood >= 1 && r.mood <= 5) mood.push({ t, v: r.mood, category });
    if (typeof r.energy === "number" && Number.isFinite(r.energy) && r.energy >= 1 && r.energy <= 5) energy.push({ t, v: r.energy, category });
    if (typeof r.sleep_hours === "number" && Number.isFinite(r.sleep_hours) && r.sleep_hours >= 0) {
      sleep_hours.push({ t, v: r.sleep_hours, category });
    }
  }

  return { mood, energy, sleep_hours };
}
